import {isIanaTimeZone} from '@fitro/date-time'
import {publicFetch, resolvePublicApiBaseUrl} from '@fitro/customer-host/api'

/** A place where the studio's activities happen; a room inside the studio has no address of its own. */
export type PublicPartnerLocation = {
    id: string
    city: string | null
    postalCode: string | null
    street: string | null
}

export type PublicPartnerAddress = {
    city: string | null
    directions: string | null
    latitude: number | null
    longitude: number | null
    postalCode: string | null
    street: string | null
}

export type PublicPartnerContact = {
    email: string | null
    phone: string | null
    websiteUrl: string | null
}

export type PublicPartnerSocialLinks = {
    facebookUrl: string | null
    instagramUrl: string | null
}

export type PublicPartnerOpeningHour = {
    dayOfWeek: number
    endTime: string
    startTime: string
    timeZone: string | null
    type: string | null
}

export type PublicPartnerProfile = {
    active: true
    timeZone: string
    address: PublicPartnerAddress
    contact: PublicPartnerContact
    description: string | null
    descriptionHtml?: string | null
    displayName: string
    partnerId: string | null
    logoUrl: string | null
    /** Activity category keys, the primary one first. */
    categories: string[]
    primaryCategory: string | null
    /** Fitro lists the studio for search engines and its pages have the minimum content. */
    seoIndexable: boolean
    openingHours: PublicPartnerOpeningHour[]
    locations: PublicPartnerLocation[]
    slug: string
    socialLinks: PublicPartnerSocialLinks
}

export type PublicPartnerProfileRequestErrorKind = 'configuration' | 'http' | 'network' | 'payload'

class PublicPartnerProfileRequestError extends Error {
    constructor(readonly kind: PublicPartnerProfileRequestErrorKind) {
        super(`Public partner profile request failed: ${kind}`)
        this.name = 'PublicPartnerProfileRequestError'
    }
}

function nullableString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null
}

function stringList(value: unknown): string[] {
    return Array.isArray(value) ? value.map(nullableString).filter((item): item is string => item !== null) : []
}

function nullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function normalizePartnerId(value: unknown): string | null {
    const candidate = nullableString(value)
    return candidate && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate) ? candidate.toLowerCase() : null
}

function normalizeAddress(value: unknown): PublicPartnerAddress {
    const candidate = value && typeof value === 'object' ? value as Record<string, unknown> : {}
    return {
        city: nullableString(candidate.city),
        directions: nullableString(candidate.directions),
        latitude: nullableNumber(candidate.latitude),
        longitude: nullableNumber(candidate.longitude),
        postalCode: nullableString(candidate.postalCode),
        street: nullableString(candidate.street),
    }
}

function normalizeLocations(value: unknown): PublicPartnerLocation[] {
    if (!Array.isArray(value)) return []
    return value.flatMap((item) => {
        const candidate = item && typeof item === 'object' ? item as Record<string, unknown> : {}
        const id = nullableString(candidate.id)
        return id
            ? [{id, city: nullableString(candidate.city), postalCode: nullableString(candidate.postalCode), street: nullableString(candidate.street)}]
            : []
    })
}

function normalizeContact(value: unknown): PublicPartnerContact {
    const candidate = value && typeof value === 'object' ? value as Record<string, unknown> : {}
    return {
        email: nullableString(candidate.email),
        phone: nullableString(candidate.phone),
        websiteUrl: nullableString(candidate.websiteUrl),
    }
}

function normalizeSocialLinks(value: unknown): PublicPartnerSocialLinks {
    const candidate = value && typeof value === 'object' ? value as Record<string, unknown> : {}
    return {
        facebookUrl: nullableString(candidate.facebookUrl),
        instagramUrl: nullableString(candidate.instagramUrl),
    }
}

function normalizeOpeningHours(value: unknown): PublicPartnerOpeningHour[] {
    if (!Array.isArray(value)) return []

    return value
        .map((item): PublicPartnerOpeningHour | null => {
            if (!item || typeof item !== 'object') return null
            const candidate = item as Record<string, unknown>
            const dayOfWeek = candidate.dayOfWeek
            const startTime = nullableString(candidate.startTime)
            const endTime = nullableString(candidate.endTime)
            if (
                typeof dayOfWeek !== 'number'
                || !Number.isInteger(dayOfWeek)
                || dayOfWeek < 1
                || dayOfWeek > 7
                || !startTime
                || !endTime
            ) {
                return null
            }

            return {
                dayOfWeek,
                endTime,
                startTime,
                timeZone: nullableString(candidate.timeZone),
                type: nullableString(candidate.type),
            }
        })
        .filter((item): item is PublicPartnerOpeningHour => Boolean(item))
}

function normalizePublicPartnerProfile(value: unknown): PublicPartnerProfile | null {
    if (!value || typeof value !== 'object') return null
    const candidate = value as Record<string, unknown>
    const slug = nullableString(candidate.slug)
    const displayName = nullableString(candidate.displayName)
    const hasPartnerId = candidate.partnerId !== undefined && candidate.partnerId !== null
    const partnerId = hasPartnerId ? normalizePartnerId(candidate.partnerId) : null
    if (!slug || !displayName || (hasPartnerId && !partnerId) || candidate.active !== true || typeof candidate.timeZone !== 'string' || !isIanaTimeZone(candidate.timeZone)) return null

    return {
        active: true,
        timeZone: String(candidate.timeZone),
        address: normalizeAddress(candidate.address),
        contact: normalizeContact(candidate.contact),
        description: nullableString(candidate.description),
        descriptionHtml: nullableString(candidate.descriptionHtml),
        displayName,
        partnerId,
        logoUrl: nullableString(candidate.logoUrl),
        categories: stringList(candidate.categories),
        primaryCategory: nullableString(candidate.primaryCategory),
        seoIndexable: candidate.seoIndexable === true,
        openingHours: normalizeOpeningHours(candidate.openingHours),
        locations: normalizeLocations(candidate.locations),
        slug,
        socialLinks: normalizeSocialLinks(candidate.socialLinks),
    }
}

export async function getPublicPartnerProfile(slug: string): Promise<PublicPartnerProfile | null> {
    try {
        return await getPublicPartnerProfileStrict(slug)
    } catch {
        return null
    }
}

export async function getPublicPartnerProfileStrict(slug: string): Promise<PublicPartnerProfile | null> {
    const normalizedSlug = slug.trim()
    if (!normalizedSlug) throw new PublicPartnerProfileRequestError('configuration')

    const baseUrl = resolvePublicApiBaseUrl()
    if (!baseUrl) throw new PublicPartnerProfileRequestError('configuration')

    const url = `${baseUrl}/api/public/partners/${encodeURIComponent(normalizedSlug)}/profile`

    let response: Response
    try {
        response = await publicFetch(url, {
            method: 'GET',
            credentials: 'omit',
            cache: 'no-store',
        })
    } catch {
        throw new PublicPartnerProfileRequestError('network')
    }

    if (response.status === 404) return null
    if (!response.ok) throw new PublicPartnerProfileRequestError('http')

    try {
        const body = await response.json()
        if (body && typeof body === 'object' && body.active === false) return null
        const profile = normalizePublicPartnerProfile(body)
        if (!profile) throw new PublicPartnerProfileRequestError('payload')
        return profile
    } catch {
        throw new PublicPartnerProfileRequestError('payload')
    }
}
